import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contentAPI } from '../services/api';
import { Clock, ChevronLeft, ChevronRight, AlertCircle, Eye, EyeOff, Send, LogOut, Sun, Moon, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import html2pdf from 'html2pdf.js';

export default function ExpressionEcriteExercise() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blocId = searchParams.get('bloc');
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const userName = user?.nom || user?.username || 'Candidat';
  
  const [bloc, setBloc] = useState(null);
  const [taches, setTaches] = useState([]);
  const [currentTache, setCurrentTache] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60 * 60); // 60 minutes total
  const [responses, setResponses] = useState({});
  const [wordCounts, setWordCounts] = useState({});
  const [startTime] = useState(Date.now());
  const [showSubject, setShowSubject] = useState({});
  const textareaRef = useRef(null);
  const finishedRef = useRef(false);

  // Caractères spéciaux français
  const specialChars = [
    'é', 'è', 'ê', 'ë', 'à',
    'â', 'ù', 'û', 'ç', 'ô',
    'œ', 'æ', '·', '»', '«',
    "'"
  ];

  useEffect(() => {
    const loadBloc = async () => {
      if (!blocId) {
        toast.error('Aucun bloc sélectionné');
        navigate('/modules/expression-ecrite');
        return;
      }

      try {
        const foundBloc = await contentAPI.getBloc('expression_ecrite', blocId);
        if (!foundBloc) throw new Error('Bloc non trouvé');
        const tasks = foundBloc.tasks || foundBloc.taches || [];

        if (tasks.length === 0) {
          toast.error('Bloc vide');
          navigate('/modules/expression-ecrite');
          return;
        }

        setBloc(foundBloc);
        setTaches(tasks);
        
        // Calculate total time
        const totalSec = tasks.reduce((sum, t) => {
          return sum + (parseInt(t.duree) || 0) * 60 + (parseInt(t.dureeSecondes) || 0);
        }, 0);
        setTimeLeft(totalSec || 60 * 60);
        
        // Initialize all subjects as visible by default
        const initialVisibility = {};
        tasks.forEach((_, index) => {
          initialVisibility[index] = true;
        });
        setShowSubject(initialVisibility);
      } catch (error) {
        console.error('Error loading bloc:', error);
        toast.error('Erreur lors du chargement');
        navigate('/modules/expression-ecrite');
      }
    };
    
    loadBloc();
  }, [blocId, navigate]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0 && taches.length > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0 && taches.length > 0 && !finishedRef.current) {
      finishedRef.current = true;
      toast.warning('Temps écoulé !');
      handleFinish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, taches.length]);

  // Update word count for current task
  useEffect(() => {
    const text = responses[currentTache] || '';
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCounts(prev => ({ ...prev, [currentTache]: words.length }));
  }, [responses, currentTache]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTextChange = (e) => {
    setResponses({
      ...responses,
      [currentTache]: e.target.value
    });
  };

  const handleTacheClick = (index) => {
    setCurrentTache(index);
  };

  const handleNext = () => {
    if (currentTache < taches.length - 1) {
      setCurrentTache(currentTache + 1);
    }
  };

  const handlePrevious = () => {
    if (currentTache > 0) {
      setCurrentTache(currentTache - 1);
    }
  };

  const insertSpecialChar = (char) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const text = responses[currentTache] || '';
      const newText = text.substring(0, start) + char + text.substring(end);
      
      setResponses({
        ...responses,
        [currentTache]: newText
      });

      // Restore cursor position
      setTimeout(() => {
        textareaRef.current.selectionStart = start + 1;
        textareaRef.current.selectionEnd = start + 1;
        textareaRef.current.focus();
      }, 0);
    }
  };

  const toggleSubject = () => {
    setShowSubject(prev => ({
      ...prev,
      [currentTache]: !prev[currentTache]
    }));
  };

  const hasAllTaskResponses = taches.length > 0 && taches.every((_, index) => (responses[index] || '').trim().length > 0);

  const handleFinish = () => {
    if (!hasAllTaskResponses) {
      toast.error('Veuillez rédiger au moins un texte pour chaque tâche avant de terminer.');
      return;
    }

    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    const completedCount = Object.keys(responses).filter(key => {
      const text = responses[key]?.trim() || '';
      return text.length > 0;
    }).length;
    
    navigate('/modules/expression-ecrite/results', {
      state: {
        moduleName: 'expression_ecrite',
        blocName: bloc?.name || 'Expression Écrite',
        blocId,
        taches,
        responses,
        wordCounts,
        timeSpent,
        completedCount
      }
    });
  };

  const formatTextForPdf = (text) => {
    if (!text.trim()) return '<p class="empty">(Aucune réponse rédigée)</p>';

    const cleaned = text
      .replace(/\r\n/g, '\n')
      .replace(/^["“]|["”]$/g, '')
      .trim();

    const paragraphs = cleaned.split(/\n+/);

    return paragraphs
      .map(paragraph => paragraph.trim())
      .filter(Boolean)
      .map(paragraph => `<p class="response-paragraph">${escapeHtml(paragraph)}</p>`)
      .join('');
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const handleDownloadPdf = () => {
    const typeLabels = { 1: 'Message court', 2: 'Narration / Blog', 3: 'Argumentation' };
    const date = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    const tasksHtml = taches.map((t, i) => {
      const wc = wordCounts[i] || 0;
      const text = responses[i] || '';
      return `
        <div class="task">
          <div class="task-header">
            <div class="task-title-row">
              <span class="task-num">Tâche ${t.tache}</span>
              <span class="task-type">${typeLabels[t.tache] || ''}</span>
            </div>
            <span class="task-wc">${wc} mot${wc !== 1 ? 's' : ''} / ${t.minMots}–${t.maxMots} recommandés</span>
          </div>
          <div class="task-block">
            <p class="section-label">Sujet de la tâche</p>
            <div class="task-subject">
              ${t.sujet || ''}
            </div>
          </div>
          <div class="task-block">
            <p class="section-label">Réponse rédigée</p>
            <div class="task-response">
              ${formatTextForPdf(text)}
            </div>
          </div>
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Expression Écrite — ${escapeHtml(bloc?.name || 'TCF Canada')}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    .pdf-wrapper { font-family: Georgia, 'Times New Roman', serif; color: #1a1a1a; background: #fff; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { border-bottom: 3px solid #2563eb; padding-bottom: 16px; margin-bottom: 32px; }
    .header h1 { font-size: 22px; color: #2563eb; margin-bottom: 8px; }
    .header p { font-size: 13px; color: #666; margin-top: 4px; }
    .task { margin-bottom: 36px; page-break-inside: avoid; }
    .task-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
    .task-title-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
    .task-num { background: #2563eb; color: #fff; font-size: 13px; font-weight: bold; padding: 3px 10px; border-radius: 20px; }
    .task-type { font-weight: bold; font-size: 15px; }
    .task-wc { margin-left: auto; font-size: 12px; color: #666; background: #f0f0f0; padding: 2px 8px; border-radius: 12px; }
    .task-block { margin-bottom: 12px; }
    .section-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; margin-bottom: 6px; font-weight: bold; }
    .task-subject { background: #eff6ff; border-left: 4px solid #2563eb; padding: 10px 14px; border-radius: 4px; font-size: 13px; color: #1e40af; line-height: 1.7; white-space: pre-wrap; }
    .task-response { font-size: 14px; line-height: 1.9; min-height: 80px; border: 1px solid #ddd; border-radius: 6px; padding: 14px 16px; background: #fafafa; }
    .task-response.empty { color: #999; font-style: italic; }
    .response-paragraph { margin-bottom: 12px; }
    .footer { margin-top: 40px; border-top: 1px solid #ddd; padding-top: 12px; font-size: 11px; color: #999; text-align: center; }
  </style>
</head>
<body>
<div class="pdf-wrapper">
  <div class="header">
    <h1>Expression Écrite — ${escapeHtml(bloc?.name || 'TCF Canada')}</h1>
    <p><strong>Candidat :</strong> ${escapeHtml(userName)}</p>
    <p>Date : ${escapeHtml(date)}</p>
  </div>
  ${tasksHtml}
  <div class="footer">TCF Canada — tcfcanada-moncombat.com</div>
</div>
</body>
</html>`;

    const element = document.createElement('div');
    element.innerHTML = html;

    const opt = {
      margin:       14,
      filename:     `expression-ecrite-${(userName || 'Candidat').replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    toast.info('Génération du PDF en cours...', { duration: 3000 });
    html2pdf().from(element).set(opt).save().then(() => {
      toast.success('PDF téléchargé avec succès !');
    });
  };

  const handleQuit = () => {
    if (window.confirm('Êtes-vous sûr de vouloir quitter l\'examen ? Votre progression sera perdue.')) {
      navigate('/modules/expression-ecrite');
    }
  };

  if (!bloc || !taches || taches.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const tache = taches[currentTache];
  const minWords = tache.minMots || 60;
  const maxWords = tache.maxMots || 120;
  const currentWordCount = wordCounts[currentTache] || 0;

  const tacheTypes = {
    1: 'Message court',
    2: 'Narration / Blog',
    3: 'Argumentation'
  };

  const getWordCountStatus = (wc, min, max) => {
    if (wc <= 0) return { percent: 0, barClass: 'bg-gray-300 dark:bg-gray-600', badgeClass: 'bg-gray-500 text-white' };
    if (wc > max) return { percent: 100, barClass: 'bg-red-500', badgeClass: 'bg-red-500 text-white' };
    if (wc >= min) return { percent: Math.min((wc / max) * 100, 100), barClass: 'bg-green-500', badgeClass: 'bg-green-500 text-white' };
    return { percent: Math.min((wc / min) * 100, 100), barClass: 'bg-orange-500', badgeClass: 'bg-orange-500 text-white' };
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Header - Fixed */}
      <header className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="max-w-full mx-auto flex items-center justify-between">
          {/* Left - Bloc name */}
          <div className="flex items-center space-x-3 flex-1">
            <div>
              <h1 className="font-semibold text-gray-900 dark:text-white text-sm md:text-base">{bloc.name}</h1>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          
          {/* Center - Timer */}
          <div className="flex items-center justify-center flex-1">
            <div className="flex items-center space-x-2 bg-blue-100 dark:bg-blue-900/40 px-4 md:px-6 py-2 rounded-full">
              <Clock className="h-5 w-5 md:h-6 md:w-6 text-blue-600 dark:text-blue-400" />
              <span className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">{formatTime(timeLeft)}</span>
            </div>
          </div>
          
          {/* Right - Empty for balance */}
          <div className="flex-1"></div>
        </div>
      </header>

      {/* Main Content - Fills remaining height */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Navigation (hidden on mobile, shown on md+) */}
        <aside className="hidden md:flex flex-col w-56 lg:w-64 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Navigation</span>
            </div>

            {/* Tâches Navigation */}
            <div className="space-y-2 mb-6">
              {taches.map((t, index) => {
                const wc = wordCounts[index] || 0;
                const isActive = currentTache === index;
                const progressState = getWordCountStatus(wc, t.minMots, t.maxMots);
                
                return (
                  <button
                    key={index}
                    onClick={() => handleTacheClick(index)}
                    data-testid={`tache-nav-${index + 1}`}
                    className={`w-full p-3 rounded-xl text-left transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="font-semibold text-sm">Tâche {t.tache}</div>
                    <div className={`text-xs mt-1 ${isActive ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                      {tacheTypes[t.tache]} - {wc}/{t.minMots}-{t.maxMots}
                    </div>
                    {/* Progress bar */}
                    <div className={`mt-2 h-1.5 rounded-full ${isActive ? 'bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <div 
                        className={`h-full rounded-full transition-all ${isActive ? 'bg-white' : progressState.barClass}`}
                        style={{ width: `${progressState.percent}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Warning Box */}
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 mb-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  Tâche vide = 0/20. Gérez votre temps et traitez les trois tâches.
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-3">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  Navigation libre entre les tâches.
                </p>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Navigation Tabs */}
          <div className="md:hidden flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-2 py-2">
            <div className="flex space-x-2 overflow-x-auto">
              {taches.map((t, index) => {
                const wc = wordCounts[index] || 0;
                const isActive = currentTache === index;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleTacheClick(index)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    T{t.tache} ({wc})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-3 md:p-6 flex flex-col">
            {/* Task Header Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-5 mb-4 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg md:text-xl font-bold flex-shrink-0">
                    {tache.tache}
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                      {tacheTypes[tache.tache]}
                    </h2>
                    <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                      {minWords}-{maxWords} mots recommandés
                    </p>
                  </div>
                </div>
                
                {/* Toggle Subject Button */}
                <button
                  onClick={toggleSubject}
                  data-testid="toggle-subject-btn"
                  className="flex items-center justify-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors self-start"
                >
                  {showSubject[currentTache] ? (
                    <>
                      <EyeOff className="h-4 w-4 text-gray-500" />
                      <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Masquer</span>
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 text-gray-500" />
                      <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Afficher</span>
                    </>
                  )}
                </button>
              </div>

              {/* Instruction - Always visible */}
              <p className="mt-4 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                {tache.instruction}
              </p>

              {/* Sujet Box - Scrollable if long */}
              {showSubject[currentTache] && (
                <div className="mt-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4 max-h-32 md:max-h-40 overflow-y-auto">
                  <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                    {tache.sujet}
                  </p>
                </div>
              )}

              {/* Image if present */}
              {showSubject[currentTache] && tache.imageUrl && (
                <div className="mt-4">
                  <img
                    src={tache.imageUrl}
                    alt="Document"
                    className="max-h-32 md:max-h-48 rounded-lg border"
                  />
                </div>
              )}
            </div>

            {/* Writing Area - Fills available space */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden flex flex-col flex-1">
              <textarea
                ref={textareaRef}
                value={responses[currentTache] || ''}
                onChange={handleTextChange}
                spellCheck={false}
                data-testid="response-textarea"
                className="flex-1 w-full px-4 md:px-5 py-4 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 resize-none dark:text-gray-200"
                placeholder="Rédigez votre texte ici..."
              />
              
              {/* Word Count Bar */}
              <div className="flex-shrink-0 px-4 md:px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center space-x-3 md:space-x-4">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs md:text-sm font-bold ${getWordCountStatus(currentWordCount, minWords, maxWords).badgeClass}`}>
                  {currentWordCount}
                </div>
                <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  / {minWords}-{maxWords}
                </span>
                <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                  <div 
                    className={`h-full rounded-full transition-all ${getWordCountStatus(currentWordCount, minWords, maxWords).barClass}`}
                    style={{ width: `${getWordCountStatus(currentWordCount, minWords, maxWords).percent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Navigation - Fixed */}
          <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="flex items-center justify-between">
              {/* Left - Quit & Dark mode buttons */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleQuit}
                  data-testid="quit-btn"
                  className="flex items-center space-x-2 px-3 py-2 rounded-xl bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="text-sm font-medium hidden sm:inline">Quitter</span>
                </button>
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle-btn"
                  className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {theme === 'dark' ? (
                    <Sun className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <Moon className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>

              {/* Center - Navigation arrows */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevious}
                  disabled={currentTache === 0}
                  data-testid="prev-btn"
                  className="p-2 md:p-3 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentTache === taches.length - 1}
                  data-testid="next-btn"
                  className="p-2 md:p-3 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-5 w-5 text-white" />
                </button>
              </div>

              {/* Mobile: Special chars */}
              <div className="flex md:hidden items-center space-x-1 overflow-x-auto max-w-[30%]">
                {specialChars.slice(0, 6).map((char, index) => (
                  <button
                    key={index}
                    onClick={() => insertSpecialChar(char)}
                    className="h-8 w-8 flex-shrink-0 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {char}
                  </button>
                ))}
              </div>

              {/* PDF Download button */}
              <button
                onClick={handleDownloadPdf}
                data-testid="download-pdf-btn"
                className="flex items-center space-x-2 px-3 md:px-4 py-2 md:py-3 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium transition-colors"
                title="Télécharger en PDF"
              >
                <FileDown className="h-4 w-4 md:h-5 md:w-5" />
                <span className="text-sm hidden sm:inline">PDF</span>
              </button>

              {/* Right - Finish button */}
              <button
                onClick={handleFinish}
                data-testid="finish-btn"
                disabled={!hasAllTaskResponses}
                className="flex items-center space-x-2 px-4 md:px-6 py-2 md:py-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-sm md:text-base">Terminer</span>
                <Send className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </div>
          </div>
        </main>

        {/* Right Sidebar - Tools (hidden on mobile and tablet, shown on lg+) */}
        <aside className="hidden lg:flex flex-col w-48 xl:w-56 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Outils</span>
            </div>

            {/* Special Characters */}
            <div className="mb-6">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Caractères spéciaux</p>
              <div className="grid grid-cols-4 xl:grid-cols-5 gap-1">
                {specialChars.map((char, index) => (
                  <button
                    key={index}
                    onClick={() => insertSpecialChar(char)}
                    className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
              </div>
            </div>

            {/* Progression */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Progression</p>
              <div className="space-y-3">
                {taches.map((t, index) => {
                  const wc = wordCounts[index] || 0;
                  const progressState = getWordCountStatus(wc, t.minMots, t.maxMots);
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Tâche {t.tache}</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{wc}/{t.minMots}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className={`h-full rounded-full transition-all ${progressState.barClass}`}
                          style={{ width: `${progressState.percent}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
